import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { ArrowRightLeft, Loader2, Archive } from "lucide-react";
import { api } from "@/lib/api";
import { LOGOS } from "@/lib/logos";
import { toast } from "sonner";

const DEFAULT_DM = { auto_export: false, destinations: [], frequency: "realtime", scope: "all", auto_archive: false };

const FREQUENCIES = [
  { value: "realtime", label: "Real-time — the moment it's approved" },
  { value: "hourly", label: "Hourly batch" },
  { value: "nightly", label: "Nightly batch · 02:00 UTC" },
  { value: "weekly", label: "Weekly batch · Mondays" },
];

const SCOPES = [
  { value: "all", label: "All transactions" },
  { value: "card_only", label: "Corporate card only" },
  { value: "reimbursements_only", label: "Reimbursements only" },
];

export const DataMovementDialog = ({ open, onOpenChange }) => {
  const [destinations, setDestinations] = useState(null);
  const [dm, setDm] = useState(DEFAULT_DM);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/integrations").then((r) => setDestinations(r.data.filter((i) => i.category === "Accounting"))).catch(() => {});
    api.get("/settings").then((r) => setDm({ ...DEFAULT_DM, ...(r.data.data_movement || {}) })).catch(() => {});
  }, [open]);

  const toggleDest = (id) => setDm((d) => ({
    ...d,
    destinations: d.destinations.includes(id) ? d.destinations.filter((x) => x !== id) : [...d.destinations, id],
  }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings", { data_movement: dm });
      toast.success(dm.auto_export
        ? "Data Movement is on — approved expenses will flow out automatically"
        : "Data Movement policy saved");
      onOpenChange(false);
    } catch {
      toast.error("Could not save the Data Movement policy");
    } finally {
      setSaving(false);
    }
  };

  const selectCls = "h-9 border-[#2a2a2a] bg-[#161616] text-[12.5px]";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg border-[#222] bg-[#141414] text-white" data-testid="data-movement-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[15px]">
            <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-[rgba(79,142,247,0.12)]">
              <ArrowRightLeft size={14} className="text-[#4F8EF7]" />
            </span>
            Export and Archive
          </DialogTitle>
          <DialogDescription className="text-[12px] text-[#A3A3A3]">
            Set the policy for what happens after an expense is approved — Aria exports and archives it for you.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* master toggle */}
          <div className="flex items-center justify-between rounded-xl border border-[#1e1e1e] bg-[#161616] px-4 py-3">
            <div>
              <div className="text-[12.5px] font-medium">Auto-export approved expenses</div>
              <div className="text-[10.5px] text-[#6b6b6b]">The moment something is approved, Aria pushes it downstream</div>
            </div>
            <Switch checked={dm.auto_export} onCheckedChange={(v) => setDm((d) => ({ ...d, auto_export: v }))}
              data-testid="dm-auto-export-toggle" className="data-[state=checked]:bg-[#4F8EF7]" />
          </div>

          <div className={`space-y-4 transition-opacity ${dm.auto_export ? "" : "pointer-events-none opacity-40"}`}>
            {/* destinations */}
            <div>
              <div className="mb-2 text-[10.5px] font-semibold uppercase tracking-wider text-[#6b6b6b]">Export to</div>
              <div className="space-y-2" data-testid="dm-destinations">
                {!destinations ? (
                  [...Array(3)].map((_, i) => <Skeleton key={i} className="h-12 rounded-xl bg-[#1a1a1a]" />)
                ) : destinations.map((integ) => (
                  <div key={integ.id} data-testid={`dm-destination-${integ.id}`}
                    className={`flex items-center gap-3 rounded-xl border px-3.5 py-2.5 ${
                      dm.destinations.includes(integ.id) && integ.connected
                        ? "border-[rgba(79,142,247,0.35)] bg-[rgba(79,142,247,0.05)]" : "border-[#1e1e1e] bg-[#161616]"}`}>
                    {LOGOS[integ.id] ? (
                      <div className="flex h-8 w-8 items-center justify-center rounded-lg border border-[#242424] bg-[#1a1a1a] p-1.5">
                        <img src={LOGOS[integ.id]} alt={integ.name} className="h-full w-full object-contain" />
                      </div>
                    ) : (
                      <div className="flex h-8 w-8 items-center justify-center rounded-lg text-[11px] font-bold"
                        style={{ background: `${integ.color}1c`, color: integ.color }}>
                        {integ.name.slice(0, 2).toUpperCase()}
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="text-[12.5px] font-medium">{integ.name}</div>
                      {integ.connected ? (
                        <div className="flex items-center gap-1.5 text-[10px] text-[#4ADE80]">
                          <span className="h-1.5 w-1.5 rounded-full bg-[#22C55E]" /> Connected
                        </div>
                      ) : (
                        <Link to="/integrations" onClick={() => onOpenChange(false)}
                          className="text-[10px] text-[#6b6b6b] hover:text-[#7FAEF9] hover:underline">
                          Not connected — set up in Integrations →
                        </Link>
                      )}
                    </div>
                    <Switch checked={dm.destinations.includes(integ.id)} disabled={!integ.connected}
                      onCheckedChange={() => toggleDest(integ.id)} data-testid={`dm-dest-toggle-${integ.id}`}
                      className="data-[state=checked]:bg-[#4F8EF7]" />
                  </div>
                ))}
              </div>
            </div>

            {/* frequency + scope */}
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <div className="mb-1.5 text-[10.5px] font-semibold uppercase tracking-wider text-[#6b6b6b]">Frequency</div>
                <Select value={dm.frequency} onValueChange={(v) => setDm((d) => ({ ...d, frequency: v }))}>
                  <SelectTrigger className={selectCls} data-testid="dm-frequency-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="border-[#222] bg-[#161616] text-white">
                    {FREQUENCIES.map((f) => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <div className="mb-1.5 text-[10.5px] font-semibold uppercase tracking-wider text-[#6b6b6b]">Which transactions</div>
                <Select value={dm.scope} onValueChange={(v) => setDm((d) => ({ ...d, scope: v }))}>
                  <SelectTrigger className={selectCls} data-testid="dm-scope-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="border-[#222] bg-[#161616] text-white">
                    {SCOPES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* auto archive */}
            <div className="flex items-center justify-between rounded-xl border border-[#1e1e1e] bg-[#161616] px-4 py-3">
              <div className="flex items-center gap-2.5">
                <Archive size={14} className="text-[#A78BFA]" />
                <div>
                  <div className="text-[12.5px] font-medium">Auto-archive after export</div>
                  <div className="text-[10.5px] text-[#6b6b6b]">Exported expenses move straight to the Archive</div>
                </div>
              </div>
              <Switch checked={dm.auto_archive} onCheckedChange={(v) => setDm((d) => ({ ...d, auto_archive: v }))}
                data-testid="dm-auto-archive-toggle" className="data-[state=checked]:bg-[#4F8EF7]" />
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}
            className="h-9 border-[#2a2a2a] bg-transparent px-4 text-[12.5px] text-[#A3A3A3] hover:bg-[#1c1c1c] hover:text-white">
            Cancel
          </Button>
          <Button onClick={save} disabled={saving} data-testid="dm-save-button"
            className="h-9 btn-gradient px-4 text-[13px] font-medium text-white disabled:opacity-40">
            {saving && <Loader2 size={14} className="mr-1.5 animate-spin" />} Save policy
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
